import React, { useEffect, useState, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import classNames from "classnames";
import { useSelector, useDispatch } from "react-redux";
import { fetchMyFacilities } from "actions/Reports";
import { switchSite } from "actions/Auth";
import Icon from "components/Icon";

const Header = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);

  const authUser = useSelector(state => state.auth.authUser);
  const myFacilities = useSelector(state => state.reports.myFacilities);
  const myFacility = useSelector(state => state.reports.myFacility);
  const bookmarks = useSelector((state) => state.app.bookmarks);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    
    const updateFade = () => {
      setFadeLeft(el.scrollLeft >= 0.5);
      setFadeRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
  
    updateFade(); // try once immediately
    const timeout = setTimeout(updateFade, 0); // then once after render
  
    el.addEventListener("scroll", updateFade);
    return () => {
      el.removeEventListener("scroll", updateFade);
      clearTimeout(timeout);
    };
  }, [myFacilities, location.pathname]);

  useEffect(() => {
    dispatch(fetchMyFacilities());
  }, [dispatch]);

  // Determine if active
  const isAllFacilities = location.pathname === "/app/facilities/allFacilities";
  const isMyFacilities = location.pathname === "/app/facilities/myFacilities";

  return (
    <div className="flex items-center min-h-25 justify-between py-9 bg-[var(--color-glass-dark)] relative">
      {/* Navigation Tabs */}
      {isAllFacilities || isMyFacilities
      ? <div className="flex items-center gap-6 ml-1">
        {authUser.sites.length > bookmarks.maxBookmarks && <h5
          id="all-facilities-h5-link"
          data-cy="all-facilities-h5-link"
          className={classNames(
            "tw-heading-5 cursor-pointer",
            isAllFacilities ? "text-[var(--color-gray-800)]" : "text-[var(--color-gray-400)]"
          )}
          onClick={() => history.push("/app/facilities/allFacilities")}
        >
          All Facilities
        </h5>}

        <h5
          id="my-facilities-h5-link"
          data-cy="my-facilities-h5-link"
          className={classNames(
            "tw-heading-5",
            authUser.sites.length > bookmarks.maxBookmarks ? "cursor-pointer" : "",
            isMyFacilities ? "text-[var(--color-gray-800)]" : "text-[var(--color-gray-400)]"
          )}
          onClick={() => history.push("/app/facilities/myFacilities")}
        >
          My Facilities
        </h5>
      </div>
      : myFacilities && myFacilities.length > 1
      ? <div className="relative flex">
          {/* Back Arrow */}
          <div
            className="px-3 cursor-pointer flex items-center justify-center"
            onClick={() => history.push("/app/facilities/myFacilities")}
            id="Header-Back-Button"
            data-cy="Header-Back-Button">
            <Icon
              name="arrowLeft"
              size={24}
              className="text-[var(--color-gray-800)]"
            />
          </div>
          
          {/* Scrollable Facility Tabs */}
          <div className="relative max-w-[calc(100vw-700px)]">
            {/* Arrows */}
            {fadeLeft && (
              <button
                onClick={() => scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' })}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 text-2xl"
                aria-label="Scroll left"
              >
                ‹
              </button>
            )}
            {fadeRight && (
              <button
                onClick={() => scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' })}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 text-2xl"
                aria-label="Scroll right"
              >
                ›
              </button>
            )}

            <div
              ref={scrollRef}
              className="flex items-center gap-6 ml-1 overflow-x-auto overflow-y-hidden scrollbar-hide scroll-smooth snap-x snap-mandatory"
              style={{
                WebkitMaskImage: fadeLeft && fadeRight
                  ? 'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)'
                  : fadeLeft
                  ? 'linear-gradient(to right, transparent 0%, black 5%)'
                  : fadeRight
                  ? 'linear-gradient(to right, black 95%, transparent 100%)'
                  : 'none',
                WebkitMaskRepeat: 'no-repeat',
                maskImage: fadeLeft && fadeRight
                  ? 'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)'
                  : fadeLeft
                  ? 'linear-gradient(to right, transparent 0%, black 5%)'
                  : fadeRight
                  ? 'linear-gradient(to right, black 95%, transparent 100%)'
                  : 'none',
                maskRepeat: 'no-repeat',
                transition: 'mask-image 0.3s ease',
              }}
            >
              {myFacilities && myFacilities.length > 1 &&
                myFacilities
                .filter(m => m.id != myFacility?.id)
                .map((f) =>
                  <h6
                    key={f.name}
                    id={`facility-${f.id}-h5-link`}
                    data-cy={`facility-${f.id}-h5-link`}
                    className="tw-heading-6 whitespace-nowrap cursor-pointer shrink-0 text-[var(--color-gray-600)]"
                    onClick={() => dispatch(switchSite(f.id.toString(), false, false, false, false, `/app/facilities/facility/${f.id}`))}
                  >
                    {f.name}
                  </h6>
                )
              }
            </div>
          </div>
      </div>
      : null}
    </div>
  );
};

export default Header;